import { ChevronDown, TriangleAlert, X } from 'lucide-react';
import React, { ReactNode, UIEvent, forwardRef, useRef, useState } from 'react';
import styled from 'styled-components';

import { Command, CommandInput, CommandItem, CommandList, Spinner, cn } from '@column/ui';

import { CopyRow } from '../CopyRow';

// Shared highlight chip so inline (client) and revealed-field (server) matches
// look identical.
const MATCH_MARK_CLASS = 'rounded-[2px] bg-blue-100 text-foreground dark:bg-blue-500/25';

// Wraps every case-insensitive occurrence of `query` inside `text` in a subtle
// highlight, so a searched name / account / routing number is called out in the
// matching option row. Returns the plain string when there's no query or match.
export const highlightMatch = (text: string, query: string): ReactNode => {
  const q = query.trim();
  if (!q) {
    return text;
  }
  const lowerText = text.toLowerCase();
  const lowerQuery = q.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (let idx = lowerText.indexOf(lowerQuery); idx !== -1; idx = lowerText.indexOf(lowerQuery, cursor)) {
    if (idx > cursor) {
      parts.push(text.slice(cursor, idx));
    }
    parts.push(
      <mark key={idx} className={MATCH_MARK_CLASS}>
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    cursor = idx + q.length;
  }
  if (cursor === 0) {
    return text;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return <>{parts}</>;
};

// Renders a server search fragment (matched segments wrapped in `<em>…</em>`) with
// the same highlight chip as highlightMatch. Used to reveal a field that matched
// the query but isn't otherwise shown in the row (e.g. an ID), highlighting just
// the matched segment.
export const renderMatchFragment = (fragment: string): ReactNode => {
  const parts = fragment.split(/(<em>.*?<\/em>)/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^<em>(.*?)<\/em>$/);
        return match ? (
          <mark key={i} className={MATCH_MARK_CLASS}>
            {match[1]}
          </mark>
        ) : (
          part
        );
      })}
    </>
  );
};

// Opening uses the standard curve. Closing/collapsing (clearing a selection so the
// card collapses back into the dropdown) uses a longer, gentler ease-out — the
// Vaul drawer curve — whose softer initial ramp avoids the front-loaded "snap" of
// a stronger ease-out and lets the field glide back into place.
const TRANSITION_OPEN = 'duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]';
const TRANSITION_CLOSE = 'duration-[500ms] ease-[cubic-bezier(0.32,0.72,0,1)]';

// A full-bleed divider between the card's title and its copy rows, sharing the
// same color as the separators that sit between the copy rows themselves.
const TitleDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
`;

// The hidden side of a swap keeps its controls mounted (for the cross-fade) but
// must not be reachable by keyboard or exposed to assistive tech. `inert` removes
// the whole subtree from the tab order and the a11y tree; it isn't in this
// @types/react version yet, so apply it through a cast (empty string = present).
const inertWhenHidden = (hidden: boolean) =>
  ({ inert: hidden ? '' : undefined }) as unknown as React.HTMLAttributes<HTMLDivElement>;

// Fades its children in/out with opacity only — no height animation or overflow
// wrapper, so the dropdown's shadow/focus ring are never clipped and it doesn't
// slide. It stays mounted (inert while hidden) so it can sit in the same grid
// cell as the card and simply be revealed as the card collapses.
export const FadeSwap: React.FC<{ visible: boolean; className?: string; children: ReactNode }> = ({
  visible,
  className,
  children,
}) => (
  <div
    aria-hidden={!visible}
    {...inertWhenHidden(!visible)}
    className={cn(
      'transition-opacity motion-reduce:transition-none',
      // Visible = the dropdown coming back (a close), so ease it out gently.
      visible ? TRANSITION_CLOSE : TRANSITION_OPEN,
      visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      className
    )}
  >
    {children}
  </div>
);

// --- Selected (filled) card ---

export interface AccountCardRow {
  label: string;
  value: string;
  /** Render the value in a monospace face (for account/routing numbers). */
  monospaced?: boolean;
}

export interface SelectedAccountCardProps {
  /** Drives the open/close (grid-rows) animation. */
  open: boolean;
  title: ReactNode;
  rows: AccountCardRow[];
  /** Clears the selection, reverting the field to its dropdown state. */
  onClear: () => void;
  hasError?: boolean;
  isLoading?: boolean;
  /** Destructive banner shown at the bottom, e.g. an insufficient-funds flag. */
  alert?: string;
  className?: string;
}

// Opens/closes via the grid-template-rows 0fr→1fr technique (CSS-driven, no
// per-frame JS). The card carries its own shadow/rounded corners + overflow-
// hidden, so they stay correct as it expands and collapses (an element's own
// box-shadow isn't clipped by its overflow); the inner min-h-0 overflow wrapper
// lets the row collapse to zero. While closing it keeps showing the last content
// it had while open (via a ref snapshot), so the account info fades out with the
// card instead of emptying the instant the selection clears.
export const SelectedAccountCard: React.FC<SelectedAccountCardProps> = ({
  open,
  title,
  rows,
  onClear,
  hasError,
  isLoading,
  alert,
  className,
}) => {
  const snapshot = useRef({ title, rows, hasError, isLoading, alert });
  if (open) {
    snapshot.current = { title, rows, hasError, isLoading, alert };
  }
  const view = open ? { title, rows, hasError, isLoading, alert } : snapshot.current;

  return (
    <div
      aria-hidden={!open}
      {...inertWhenHidden(!open)}
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-lg bg-card text-card-foreground shadow-surface-raised',
        'dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]',
        'transition-all motion-reduce:transition-none',
        open ? TRANSITION_OPEN : TRANSITION_CLOSE,
        open ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
        (view.hasError || view.alert) && 'ring-1 ring-destructive',
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-2.5">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {view.isLoading ? 'Loading…' : view.title}
          </span>
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear selection"
            className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
          >
            <X className="size-4" />
          </button>
        </div>

        {!view.isLoading && view.rows.length > 0 && (
          <>
            <TitleDivider />
            <div>
              {view.rows.map((row) => (
                <CopyRow key={row.label} label={row.label} value={row.value} isMonospaced={row.monospaced} />
              ))}
            </div>
          </>
        )}

        {view.alert && (
          <div className="flex items-center gap-2 border-t border-gray-100 bg-destructive/5 px-4 py-2.5 text-xs font-medium text-destructive dark:border-white/[0.08]">
            <TriangleAlert className="size-3.5 shrink-0" />
            <span>{view.alert}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Empty (placeholder) trigger ---

interface EmptyAccountTriggerProps extends React.ComponentProps<'button'> {
  label: string;
  hasError?: boolean;
}

export const EmptyAccountTrigger = forwardRef<HTMLButtonElement, EmptyAccountTriggerProps>(
  ({ label, hasError, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-white px-3 py-2',
        'text-sm font-medium text-gray-400 shadow-btn-secondary transition-[color,box-shadow,opacity] duration-150 outline-none',
        'hover:shadow-btn-secondary-hover focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:ring-[3px] focus-visible:ring-blue-500/15',
        'dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)]',
        // While open, the popover sits directly on top of the trigger, so hide it
        // to keep the trigger's shadow from doubling up behind the popover's.
        'data-[state=open]:opacity-0',
        hasError && 'shadow-[0_0_0_1px_var(--color-destructive)] hover:shadow-[0_0_0_1px_var(--color-destructive)]',
        className
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
    </button>
  )
);
EmptyAccountTrigger.displayName = 'EmptyAccountTrigger';

// --- Search command (server-driven) ---

export interface EntitySearchOption {
  id: string;
  primary: ReactNode;
  secondary?: ReactNode;
  /** Right-aligned top line, across from `primary` (e.g. a balance amount). */
  trailingPrimary?: ReactNode;
  /** Right-aligned second line, a label sitting under `trailingPrimary`. */
  trailingSecondary?: ReactNode;
  disabled?: boolean;
  /** Trailing adornment, e.g. a lock icon for unauthorized rows. */
  adornment?: ReactNode;
}

export interface EntitySearchCommandProps {
  options: EntitySearchOption[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onSearch: (query: string) => void;
  onSelect: (id: string) => void;
  onScrollEnd?: () => void;
  footer?: ReactNode;
}

export const EntitySearchCommand: React.FC<EntitySearchCommandProps> = ({
  options,
  isLoading,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results found',
  onSearch,
  onSelect,
  onScrollEnd,
  footer,
}) => {
  const [query, setQuery] = useState('');

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 64) {
      onScrollEnd?.();
    }
  };

  return (
    <Command shouldFilter={false} className="bg-transparent">
      <CommandInput
        value={query}
        onValueChange={(value) => {
          setQuery(value);
          onSearch(value);
        }}
        placeholder={searchPlaceholder}
      />
      <CommandList onScroll={handleScroll} className="p-1">
        {options.length === 0 && !isLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        )}
        {options.map((option) => (
          <CommandItem
            key={option.id}
            value={option.id}
            data-cy={`dropdown-option-${option.id}`}
            disabled={option.disabled}
            onSelect={() => !option.disabled && onSelect(option.id)}
            className="flex items-start gap-3"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate">{option.primary}</span>
              {option.secondary && (
                <span className="truncate text-xs font-normal text-muted-foreground">{option.secondary}</span>
              )}
            </div>
            {(option.trailingPrimary || option.trailingSecondary) && (
              // Right-aligned column; shrink-0 + whitespace-nowrap keep it fixed, and the
              // left margin gives the primary/secondary room to truncate before colliding.
              <div className="flex shrink-0 flex-col items-end gap-0.5 pl-6 text-right">
                {option.trailingPrimary && <span className="whitespace-nowrap">{option.trailingPrimary}</span>}
                {option.trailingSecondary && (
                  <span className="text-xs font-normal whitespace-nowrap text-muted-foreground">
                    {option.trailingSecondary}
                  </span>
                )}
              </div>
            )}
            {option.adornment}
          </CommandItem>
        ))}
        {isLoading && (
          <div className="flex justify-center py-3">
            <Spinner />
          </div>
        )}
      </CommandList>
      {footer}
    </Command>
  );
};
